'use client';
import { useEffect, useRef } from 'react';
import { getAmplitudeBrowserOptions, getAmplitudeKey, getEventOptions } from './utils';
import { add, init, logEvent, setUserId } from '@amplitude/analytics-browser';
import { PRODUCT, TIMERS_IN_MS } from '@/utils/constants';
import { sendAmplitudeIdentityEvent } from './events/general/identity';
import { useStore } from '@/store/store';
import { getCookieValue, setCookie } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies/constants';
import { getConfig } from '@/utils/config/config';
import { useWalletUm } from '@/hooks/use-wallet-um';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useTrackingContext } from '@/tracking/context/use-tracking-context';
import {
    createCasinoEventContextEnrichmentPlugin,
    getAmplitudeGlobalContextSnapshot,
    mergeAmplitudeGlobalContextIntoEventProperties,
    setAmplitudeGlobalContextSnapshot
} from '@/tracking/amplitude/plugins/casino-event-context-enrichment-plugin';
import { AMPLITUDE_EXPOSURE_EVENT } from './events/general/experiment-exposure';

let isAmplitudeSetupComplete = false;

const useInitAmplitude = () => {
    const { rootDomain } = getConfig();
    const key = getAmplitudeKey();
    const accountID = useStore((state) => state.userContext.accountSettings.accountID);
    const deviceIdCookieValue = getCookieValue(COOKIES.AMP_DEVICE_ID);
    const sessionIdCookieValue = getCookieValue(COOKIES.AMP_SESSION_ID);
    const deviceId = deviceIdCookieValue || crypto.randomUUID();
    const sessionId = sessionIdCookieValue || Date.now().toString();

    useEffect(() => {
        if (!isAmplitudeSetupComplete) {
            add(createCasinoEventContextEnrichmentPlugin());
            init(key, accountID, getAmplitudeBrowserOptions({ deviceId, sessionId }));
            isAmplitudeSetupComplete = true;
        }
        if (!deviceIdCookieValue) {
            setCookie(COOKIES.AMP_DEVICE_ID, deviceId, TIMERS_IN_MS.ONE_YEAR, '/', rootDomain);
        }
        if (!sessionIdCookieValue) {
            setCookie(COOKIES.AMP_SESSION_ID, sessionId, undefined, '/', rootDomain);
        }
    }, []);
};

export const useAmplitude = () => {
    useInitAmplitude();
    const trackingContext = useTrackingContext();
    const { region } = useGlobalContext();
    const accountID = useStore((state) => state.userContext.accountSettings.accountID);
    const isLoggedIn = useStore((state) => state.userContext.isLoggedIn);
    const isWaitingForBalance = useRef(true);
    const eventQueue = useRef<any[]>([]);
    const { balance, getWalletBalance } = useWalletUm();
    const renderHeader = true; // TODO: do it correctly

    useEffect(() => {
        if (isLoggedIn) {
            getWalletBalance();
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (accountID) {
            setUserId(accountID);
        }

        sendAmplitudeIdentityEvent({
            userLocation: region,
            stateSelected: region
        });
    }, [region, accountID]);

    useEffect(() => {
        const base = {
            Product: trackingContext.product,
            'Page Name': trackingContext.route.pageName,
            Balance:
                trackingContext.user.isLoggedIn && Number.isFinite(balance?.playableBalance)
                    ? Math.round(balance!.playableBalance)
                    : null,
            'Login Status': trackingContext.user.loginStatus,
            'Site Version': trackingContext.platform.siteVersion,
            'Site Platform': trackingContext.platform.sitePlatform,
            Jurisdiction: trackingContext.user.jurisdiction ?? '',
            'National Jurisdiction': trackingContext.user.nationalJurisdiction,
            // setAmplitudeGlobalContextSnapshot replaces the whole snapshot, so preserve the loyalty
            // level (set async via rewards-hub shared state) when logged in; clear it on logout.
            'Casino Loyalty Level': trackingContext.user.isLoggedIn
                ? (getAmplitudeGlobalContextSnapshot()['Casino Loyalty Level'] ?? null)
                : null
        };

        // Mirror existing behavior: add web-only URL fields when not in wrapper; else add native-only fields.
        if (!trackingContext.platform.isAndroidWrapper && !trackingContext.platform.isIosWrapper) {
            setAmplitudeGlobalContextSnapshot({
                ...base,
                'Full URL': trackingContext.route.fullUrl ?? '',
                Hostname: trackingContext.route.hostname ?? '',
                'Page Path': trackingContext.route.pagePath,
                'Page Title': trackingContext.route.pageTitle ?? ''
            });
            return;
        }

        setAmplitudeGlobalContextSnapshot({
            ...base,
            'Page Path': trackingContext.route.pagePath,
            'App Name': '',
            'App ID': '',
            'Android Distribution Method': trackingContext.platform.androidDistributionMethod ?? '',
            'Next.js': true
        });
    }, [
        trackingContext,
        balance?.playableBalance,
        trackingContext.user.isLoggedIn,
        trackingContext.user.loginStatus,
        trackingContext.user.jurisdiction,
        trackingContext.user.nationalJurisdiction,
        trackingContext.product,
        trackingContext.route.pageName,
        trackingContext.route.fullUrl,
        trackingContext.route.hostname,
        trackingContext.route.pagePath,
        trackingContext.route.pageTitle,
        trackingContext.platform.isAndroidWrapper,
        trackingContext.platform.isIosWrapper,
        trackingContext.platform.siteVersion,
        trackingContext.platform.sitePlatform,
        trackingContext.platform.androidDistributionMethod
    ]);

    const sendEventToAmplitude = (props: any) => {
        let eventName = props?.event;
        if (!eventName) {
            console.warn('[Amplitude] No event name found', props); // TODO: do we want to log all props? expensive?
            return;
        }

        if (isWaitingForBalance.current) {
            console.log('[Amplitude] Waiting for balance to be fetched', props);
            eventQueue.current.push(props);
            return;
        }

        if (process.env.PRODUCT === PRODUCT.XSELL && eventName !== AMPLITUDE_EXPOSURE_EVENT) {
            eventName = `CAS XS ${eventName}`;
        }

        // Merge globals here so events keep context even if another package re-inits Amplitude and drops plugins.
        const enrichedProps = mergeAmplitudeGlobalContextIntoEventProperties(props);
        logEvent(eventName, enrichedProps, getEventOptions());
    };

    const flushQueue = () => {
        eventQueue.current?.forEach((eventProps) => sendEventToAmplitude(eventProps));
        eventQueue.current = [];
    };

    useEffect(() => {
        const product = process.env.PRODUCT;
        if (product === PRODUCT.CASINO || product === PRODUCT.POKER) {
            if (!isLoggedIn || !renderHeader) {
                isWaitingForBalance.current = false;
                flushQueue();
                return () => {};
            }

            if (!Number.isFinite(balance?.playableBalance)) {
                return () => {};
            }
        }

        if (product === PRODUCT.XSELL && isLoggedIn && Number.isNaN(balance?.playableBalance)) {
            isWaitingForBalance.current = false; // TODO: playable balance is NaN, why do we set isWaiting to false?
            return () => {};
        }

        isWaitingForBalance.current = false;
        flushQueue();
    }, [isLoggedIn, balance, renderHeader]);

    return {
        sendEventToAmplitude
    };
};
